import { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

const TEAL = "#2ec4b6";
const NAVY = "#1F2E3B";
const CREAM = "#F7F4EE";
const MUTED = "#536270";

function Field({ label, name, value, onChange, type = "text", optional, placeholder }: {
  label: string; name: string; value: string; onChange: (v: string) => void;
  type?: "text" | "textarea"; optional?: boolean; placeholder?: string;
}) {
  const base = "w-full rounded-xl px-4 py-3 text-sm outline-none border transition-all";
  const style = { borderColor: "#d6cfc5", background: "#fff", color: NAVY, fontFamily: "DM Sans, sans-serif" };
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: NAVY }}>
        {label}
        {optional && <span className="ml-1 font-normal" style={{ color: MUTED }}>(optional)</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          className={`${base} resize-vertical`}
          style={{ ...style, minHeight: 80 }}
          onFocus={e => (e.target.style.borderColor = TEAL)}
          onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
        />
      ) : (
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          className={base}
          style={style}
          onFocus={e => (e.target.style.borderColor = TEAL)}
          onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
        />
      )}
    </div>
  );
}

function parseOutput(text: string) {
  const lines = text.split("\n");
  const result: Array<{ heading: boolean; text: string }> = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { result.push({ heading: false, text: "" }); continue; }
    if (
      trimmed.startsWith("##") ||
      /^[A-Z][A-Z\s:&/]{5,}$/.test(trimmed) ||
      /^\*\*[^*]+\*\*$/.test(trimmed)
    ) {
      result.push({ heading: true, text: trimmed.replace(/^#+\s*/, "").replace(/\*\*/g, "") });
    } else {
      result.push({ heading: false, text: trimmed });
    }
  }
  return result;
}

export default function AdminClubs() {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(false);

  const [clubName, setClubName] = useState("");
  const [sport, setSport] = useState("");
  const [location, setLocation] = useState("");
  const [league, setLeague] = useState("");
  const [memberCount, setMemberCount] = useState("");
  const [ageGroups, setAgeGroups] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [currentOnline, setCurrentOnline] = useState("");
  const [painPoints, setPainPoints] = useState("");
  const [goals, setGoals] = useState("");
  const [keyContact, setKeyContact] = useState("");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    fetch("/api/admin/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setAuthed(true); setAuthError(false); }
        else { setAuthError(true); }
      })
      .catch(() => setAuthError(true));
  }

  async function handleResearch(e: React.FormEvent) {
    e.preventDefault();
    if (!clubName) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await fetch("/api/admin/clubs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password,
          clubName, sport, location, league, memberCount, ageGroups,
          websiteUrl, facebookUrl, instagramUrl, currentOnline,
          painPoints, goals, keyContact, notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Research failed");
      setOutput(data.brief);
      setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err: any) {
      toast({ title: "Research failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  function copyFull() {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast({ title: "Copied", description: "Full brief copied to clipboard." });
  }

  function copyProposal() {
    if (!output) return;
    const match = output.match(/PROPOSAL EMAIL[\s\S]*/i);
    if (match) {
      navigator.clipboard.writeText(match[0].replace(/^PROPOSAL EMAIL[:\s]*/i, "").trim());
      toast({ title: "Copied", description: "Proposal email copied to clipboard." });
    } else {
      navigator.clipboard.writeText(output);
      toast({ title: "Copied", description: "Brief copied." });
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <div className="w-full max-w-sm p-8 rounded-2xl" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
          <div className="flex items-center gap-2 mb-6">
            <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
              <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
              Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
            </span>
            <span className="ml-2 text-xs font-medium" style={{ color: MUTED }}>Admin</span>
          </div>
          <h1 className="text-base font-semibold mb-5" style={{ color: NAVY }}>Clubs Research — Private Access</h1>
          <form onSubmit={handleAuth} className="flex flex-col gap-4">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              className="w-full rounded-xl px-4 py-3 text-sm outline-none border"
              style={{ borderColor: authError ? "#e05" : "#d6cfc5", background: CREAM, color: NAVY }}
            />
            {authError && <p className="text-xs" style={{ color: "#c0392b" }}>Incorrect password.</p>}
            <button type="submit" className="rounded-full py-3 text-sm font-medium"
              style={{ background: NAVY, color: "#fff" }}>
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: "'DM Sans', sans-serif", color: NAVY }}>
      <header style={{ background: "rgba(247,244,238,.95)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3">
          <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
          <span className="text-xs px-2 py-1 rounded-full ml-1 font-medium" style={{ background: "#e4ddd2", color: MUTED }}>
            Clubs Research
          </span>
          <a href="/admin/research" className="ml-auto text-xs underline" style={{ color: MUTED }}>
            → Business Research
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", fontWeight: 600, color: NAVY, marginBottom: 4 }}>
            Sporting Club Research
          </h1>
          <p style={{ color: MUTED, fontSize: "0.9rem" }}>Enter what you know about the club and run a research brief — online presence, gaps, and a tailored proposal.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">

          <form onSubmit={handleResearch} className="flex flex-col gap-4 rounded-2xl p-7" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
            <h2 className="font-semibold text-base mb-1" style={{ color: NAVY }}>Club Details</h2>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Club name" name="clubName" value={clubName} onChange={setClubName} placeholder="e.g. Caboolture FC" />
              <Field label="Sport / code" name="sport" value={sport} onChange={setSport} placeholder="e.g. Football (soccer)" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Location" name="location" value={location} onChange={setLocation} placeholder="e.g. Caboolture QLD" />
              <Field label="League / association" name="league" value={league} onChange={setLeague} placeholder="e.g. Football Queensland" optional />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Approx. member count" name="memberCount" value={memberCount} onChange={setMemberCount} placeholder="e.g. 400 members" optional />
              <Field label="Age groups" name="ageGroups" value={ageGroups} onChange={setAgeGroups} placeholder="e.g. U6–U18 + seniors" optional />
            </div>

            <div style={{ borderTop: "1px solid #e4ddd2", paddingTop: 16, marginTop: 4 }}>
              <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: MUTED }}>Online Presence</h3>
              <div className="flex flex-col gap-4">
                <Field label="Website URL" name="websiteUrl" value={websiteUrl} onChange={setWebsiteUrl} optional placeholder="https://..." />
                <Field label="Facebook page URL" name="facebookUrl" value={facebookUrl} onChange={setFacebookUrl} optional placeholder="https://facebook.com/..." />
                <Field label="Instagram handle" name="instagramUrl" value={instagramUrl} onChange={setInstagramUrl} optional placeholder="@clubhandle" />
                <Field label="How are they currently found online?" name="currentOnline" value={currentOnline} onChange={setCurrentOnline} type="textarea"
                  placeholder="e.g. Old website, active Facebook, not on Google Maps, no GMB profile..." />
              </div>
            </div>

            <div style={{ borderTop: "1px solid #e4ddd2", paddingTop: 16, marginTop: 4 }}>
              <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: MUTED }}>Needs & Goals</h3>
              <div className="flex flex-col gap-4">
                <Field label="Pain points / what's not working" name="painPoints" value={painPoints} onChange={setPainPoints} type="textarea"
                  placeholder="e.g. Can't attract junior players, no easy way for parents to register, committee volunteers can't update the website..." />
                <Field label="What they want to achieve" name="goals" value={goals} onChange={setGoals} type="textarea"
                  placeholder="e.g. More registrations, better communication with members, look professional for sponsors..." />
              </div>
            </div>

            <div style={{ borderTop: "1px solid #e4ddd2", paddingTop: 16, marginTop: 4 }}>
              <Field label="Key contact (name + role)" name="keyContact" value={keyContact} onChange={setKeyContact} optional placeholder="e.g. Jamie Brooks — President" />
              <div className="mt-4">
                <Field label="Any other notes" name="notes" value={notes} onChange={setNotes} type="textarea" optional
                  placeholder="Anything else worth knowing..." />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !clubName}
              className="mt-2 w-full rounded-full py-4 text-base font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: TEAL, color: "#fff" }}>
              {loading ? `Researching ${clubName}…` : "Run Club Research"}
            </button>
          </form>

          <div ref={outputRef} className="rounded-2xl p-7 min-h-96" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h2 className="font-semibold text-base" style={{ color: NAVY }}>Research Brief</h2>
              {output && (
                <div className="flex gap-2">
                  <button onClick={copyFull}
                    className="rounded-full px-4 py-2 text-xs font-medium transition-all hover:-translate-y-0.5"
                    style={{ background: TEAL, color: "#fff" }}>
                    Copy Full Brief
                  </button>
                  <button onClick={copyProposal}
                    className="rounded-full px-4 py-2 text-xs font-medium transition-all hover:-translate-y-0.5"
                    style={{ background: NAVY, color: "#fff" }}>
                    Copy Proposal Email
                  </button>
                </div>
              )}
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="animate-spin rounded-full border-4 border-t-transparent"
                  style={{ width: 44, height: 44, borderColor: `${TEAL} transparent ${TEAL} ${TEAL}` }} />
                <p className="text-sm" style={{ color: MUTED }}>Researching {clubName}…</p>
                <p className="text-xs" style={{ color: MUTED }}>This may take 30–60 seconds</p>
              </div>
            )}

            {!loading && !output && (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div style={{ fontSize: "2.5rem" }}>🏆</div>
                <p className="text-sm" style={{ color: MUTED }}>Fill in the club details and click Run Club Research.</p>
              </div>
            )}

            {!loading && output && (
              <div className="text-sm leading-relaxed" style={{ color: NAVY }}>
                {parseOutput(output).map((block, i) => {
                  if (!block.text) return <div key={i} className="h-3" />;
                  if (block.heading) {
                    return (
                      <h3 key={i} className="font-semibold text-sm uppercase tracking-wide mt-6 mb-2 pb-1"
                        style={{ color: TEAL, borderBottom: `1px solid #e4ddd2`, letterSpacing: "0.08em" }}>
                        {block.text}
                      </h3>
                    );
                  }
                  const text = block.text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\*(.*?)\*/g, "<em>$1</em>");
                  return <p key={i} className="mb-1" style={{ lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: text }} />;
                })}
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
